import React from 'react';
import { Edit, Loader2, Plus, Trash2 } from 'lucide-react';
import { CarrierIntegration } from '@/core/api/types';
import { Company } from '../types';
import { getCompanyName } from '../utils';
import { useCarriers } from '@/core/api/queries/carriers';
import { useUIState } from '@/core/UIStateProvider';
import { useDeleteCarrierIntegration } from '@/core/api/queries/carriers';
import { useCompanies } from '@/core/api/queries';

interface CarrierIntegrationsTabProps {
  setShowAddCarrierIntegration: (show: boolean) => void;
  setShowEditCarrierIntegration: (show: boolean) => void;
  setCarrierIntegrationToEdit: (carrierIntegrationId: string) => void;
}

export const CarrierIntegrationsTab: React.FC<CarrierIntegrationsTabProps> = ({
  setShowAddCarrierIntegration,
  setShowEditCarrierIntegration,
  setCarrierIntegrationToEdit,
}) => {
  const companies = useCompanies();

  const companiesData = (companies.data as Company[]) || [];
  const { addNotification } = useUIState();

  const carriers = useCarriers();
  const deleteCarrierIntegration = useDeleteCarrierIntegration();

  const handleDeleteCarrierIntegration = async (carrierIntegrationId: string): Promise<void> => {
    try {
      await deleteCarrierIntegration.mutateAsync(carrierIntegrationId);
      addNotification('success', 'Carrier integration deleted successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete carrier integration';
      addNotification('error', errorMessage);
    }
  };
  const handleEditCarrierIntegration = (carrierIntegrationId: string): void => {
    setShowEditCarrierIntegration(true);
    setCarrierIntegrationToEdit(carrierIntegrationId);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Carrier Integrations</h2>
        <button
          onClick={() => setShowAddCarrierIntegration(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus size={18} />
          <span>Add Carrier Integration</span>
        </button>
      </div>
      {carriers.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading carriers...</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Carrier</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">LSP</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Active</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Created</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {carriers.data?.map((carrier: CarrierIntegration) => (
                <tr key={carrier.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-900">{carrier.carrierName}</div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-600">
                      {carrier.lspId ? getCompanyName(carrier.lspId, companiesData) : 'Not assigned'}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        carrier.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {carrier.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-500">{new Date(carrier.createdAt).toLocaleDateString()}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-end space-x-2">
                      {/* TODO: Add edit carrier integration */}
                      <button
                        onClick={() => handleEditCarrierIntegration(carrier.id)}
                        className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Edit size={16} className="text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteCarrierIntegration(carrier.id)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
